import ErrorComponent from "@/components/atoms/ErrorComponent";
import IconButton from "@/components/atoms/IconButton";
import CalendarBoard from "@/components/molecules/calendar/CalendarBoard";
import { CalendarFiltersButton } from "@/components/molecules/calendar/CalendarFilters";
import {
  MontageCalendarFilters,
  MontageCalendarFiltersPanel,
  countActiveMontageCalendarFilters,
  toMontageCalendarParams,
} from "@/components/molecules/calendar/MontageCalendarFilters";
import MontageMeetingModal, {
  MontageMeetingModalData,
} from "@/components/molecules/modals/MontageMeetingModal";
import StoreCompanyMeetingModal from "@/components/molecules/modals/company/StoreCompanyMeetingModal";
import { UNTITLED_MEETING, meetingEndAt } from "@/helpers/MeetingHelpers";
import { checkIfUserHasRoles } from "@/helpers/UserHelpers";
import { getInitialUrlState, useTableFilters } from "@/hooks/useTableFilters";
import MontageMeetingStatus from "@/interfaces/MontageMeetingStatus";
import { useGetMontageMeetingsQuery } from "@/services/montageMeetings";
import { selectCurrentUser } from "@/services/slices/authSlice";
import { useGetCalendarFilterableQuery } from "@/services/users";
import { faPalette, faPlus } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { EventClickArg, EventInput } from "@fullcalendar/core";
import { ElementRef, FC, useRef, useState } from "react";
import { Col, Dropdown, Form } from "react-bootstrap";
import { useSelector } from "react-redux";

/** Créneaux restreints et rendez-vous sans statut : gris neutre. */
const NEUTRAL_COLOR = "#748194";
const NEUTRAL_TEXT = "#000000";

/** Une ligne vide se dit, elle ne se cache pas : un RDV sans CSM est une information. */
const UNASSIGNED = "Non assigné";

const fullName = (user?: { firstname: string; lastname: string } | null) =>
  user ? `${user.firstname} ${user.lastname}` : null;

const CalendarMontageView: FC = () => {
  const user = useSelector(selectCurrentUser);
  const ModalRef = useRef<ElementRef<typeof MontageMeetingModal>>(null);
  const StoreMeetingModalRef =
    useRef<ElementRef<typeof StoreCompanyMeetingModal>>(null);
  const [query, setQuery] = useState("");

  // Panneau « Filtres » : statut montage, commercial, télépro et CSM, gardés
  // dans l'URL. Un admin ou un CSM choisit commercial et télépro parmi tout le
  // monde, puisqu'il voit tous les rendez-vous ; un commercial garde son
  // entourage, comme sur le calendrier commercial.
  const [filters, setFilters] = useState<MontageCalendarFilters>(() =>
    getInitialUrlState(),
  );
  const [showFilters, setShowFilters] = useState(false);
  const { onFiltersChange } = useTableFilters({
    tableName: "calendarMontage",
    state: filters as any,
    onStateChange: setFilters as any,
    storage: "url",
  });
  const canCreate = checkIfUserHasRoles(user, ["ADMIN", "CSM", "AE"]);
  const seesEveryone = checkIfUserHasRoles(user, ["ADMIN", "CSM"]);
  const { data: filterablePool } = useGetCalendarFilterableQuery(undefined, {
    skip: seesEveryone,
  });
  const showUserFilters = seesEveryone || (filterablePool?.length ?? 0) > 0;

  const { data, isLoading, isFetching, isError, error } =
    useGetMontageMeetingsQuery(toMontageCalendarParams(filters));

  if (!checkIfUserHasRoles(user, ["ADMIN", "CSM", "AE", "SDR"])) {
    return (
      <ErrorComponent error={{ status: 403, data: {} }} absolute returnButton />
    );
  }

  if (isError) {
    return <ErrorComponent error={error} absolute />;
  }

  const meetings = data ?? [];

  // La couleur d'un événement vient désormais de son statut, qui porte les
  // siennes : plus besoin de palette générée ni de calcul de contraste.
  const usedStatuses = Array.from(
    meetings.reduce((entries, meeting) => {
      if (meeting.restricted) return entries;
      const status = meeting.montageStatus;
      if (status && !entries.has(status.id)) entries.set(status.id, status);
      return entries;
    }, new Map<string, MontageMeetingStatus>()),
  )
    .map(([, status]) => status)
    .sort((first, second) => first.order - second.order);

  // Les couleurs et la légende se calculent sur TOUS les rendez-vous, jamais sur
  // le résultat filtré : sinon la couleur d'un CSM changerait à chaque frappe.
  const search = query.trim().toLowerCase();
  const visibleMeetings = search
    ? meetings.filter((meeting) => {
        // Un créneau restreint n'expose ni entreprise ni détail : il n'a rien à
        // faire correspondre, et le garder afficherait un résultat muet.
        if (meeting.restricted) return false;
        const csm = meeting.csmOwner
          ? `${meeting.csmOwner.firstname} ${meeting.csmOwner.lastname}`
          : "";
        return (
          (meeting.company?.name ?? meeting.title ?? "")
            .toLowerCase()
            .includes(search) || csm.toLowerCase().includes(search)
        );
      })
    : meetings;

  const events: EventInput[] = visibleMeetings.map((meeting) => {
    if (meeting.restricted) {
      // Gris neutre, jamais la couleur du statut : ces créneaux appartiennent à
      // des entreprises que l'utilisateur n'a pas le droit de voir, et leur
      // couleur trahirait leur état commercial.
      return {
        id: meeting.id,
        title: "",
        start: meeting.startAt,
        end: meetingEndAt(meeting),
        durationMinutes: meeting.durationMinutes ?? 60,
        backgroundColor: NEUTRAL_COLOR,
        borderColor: NEUTRAL_COLOR,
        textColor: NEUTRAL_TEXT,
        interactive: false,
        restricted: true,
      };
    }

    const backgroundColor =
      meeting.montageStatus?.backgroundColor ?? NEUTRAL_COLOR;
    const textColor = meeting.montageStatus?.textColor ?? NEUTRAL_TEXT;
    const authorName = meeting.author
      ? `${meeting.author.firstname} ${meeting.author.lastname}`
      : null;
    const csmOwnerName = meeting.csmOwner
      ? `${meeting.csmOwner.firstname} ${meeting.csmOwner.lastname}`
      : null;
    const aeName = fullName(meeting.company?.ae?.user);
    const sdrName = fullName(meeting.company?.sdr?.user);
    // Sans entreprise, le titre tient lieu de nom (spec 2026-10-08, §2.7).
    const headline = meeting.company?.name ?? meeting.title ?? UNTITLED_MEETING;
    const isStandalone = !meeting.companyId;

    return {
      id: meeting.id,
      title: `${headline} — ${csmOwnerName ?? "Non assigné"}${
        authorName ? ` (par ${authorName})` : ""
      }`,
      start: meeting.startAt,
      end: meetingEndAt(meeting),
      durationMinutes: meeting.durationMinutes ?? 60,
      backgroundColor,
      borderColor: backgroundColor,
      textColor,
      companyId: meeting.companyId,
      montageStatusId: meeting.montageStatusId,
      montageStatusName: meeting.montageStatus?.name ?? null,
      companyName: headline,
      standalone: isStandalone,
      authorId: meeting.authorId,
      authorName,
      canManage: meeting.canManage ?? false,
      csmOwnerId: meeting.csmOwnerId,
      csmOwnerName,
      aeName,
      sdrName,
      // Le titre reste court : en vue mois FullCalendar le tronque. Le détail
      // complet vit dans `details`, que la fenêtre d'une journée déplie.
      headline,
      details: [
        { label: "Statut", value: meeting.montageStatus?.name ?? UNASSIGNED },
        // Sans fiche, ni commercial ni télépro : « Non assigné » ferait croire
        // à un oubli.
        ...(isStandalone
          ? []
          : [
              { label: "Commercial", value: aeName ?? UNASSIGNED },
              { label: "Télépro", value: sdrName ?? UNASSIGNED },
            ]),
        { label: "CSM", value: csmOwnerName ?? UNASSIGNED },
      ],
    };
  });

  // Légende dans l'ordre des statuts, celui de l'écran d'administration, et non
  // par ordre alphabétique : c'est l'ordre du déroulé métier.
  const legendEntries = usedStatuses.map((status) => ({
    id: status.id,
    label: status.name,
    color: status.backgroundColor,
  }));

  if (
    meetings.some((meeting) => !meeting.restricted && !meeting.montageStatusId)
  ) {
    legendEntries.push({
      id: "no-status",
      label: "Sans statut",
      color: NEUTRAL_COLOR,
    });
  }

  const legend = legendEntries.length ? (
    <Dropdown align="end" className="me-2">
      <Dropdown.Toggle
        variant="nolina-default"
        className="d-inline-flex align-items-center justify-content-center p-0 text-600 dropdown-caret-none"
        aria-label="Afficher la légende des statuts"
        title="Légende des statuts"
        style={{ width: 44, height: 44 }}
      >
        <FontAwesomeIcon icon={faPalette} />
      </Dropdown.Toggle>

      <Dropdown.Menu
        className="border py-2 shadow-sm"
        style={{ minWidth: 220 }}
      >
        <span
          id="montage-calendar-legend-title"
          className="dropdown-header py-1 fw-bold"
        >
          Légende des statuts
        </span>
        <ul
          className="list-unstyled d-flex flex-column gap-2 px-3 py-1 mb-0"
          aria-labelledby="montage-calendar-legend-title"
        >
          {legendEntries.map((entry) => (
            <li
              key={entry.id}
              className="d-inline-flex align-items-center gap-2"
            >
              <span
                role="img"
                aria-label={`Couleur associée à ${entry.label}`}
                className="d-inline-block rounded-circle flex-shrink-0"
                style={{ width: 12, height: 12, backgroundColor: entry.color }}
              />
              <span className="text-900">{entry.label}</span>
            </li>
          ))}
        </ul>
      </Dropdown.Menu>
    </Dropdown>
  ) : undefined;

  const openMeetingModal = (data: MontageMeetingModalData) => {
    ModalRef.current?.showOrHiddenModal(data);
  };

  const onEventClick = (info: EventClickArg) => {
    if (info.event.extendedProps.restricted) return;

    openMeetingModal({
      id: info.event.id,
      companyId: info.event.extendedProps.companyId ?? null,
      companyName: info.event.extendedProps.companyName ?? "Entreprise",
      startAt: info.event.startStr,
      durationMinutes: info.event.extendedProps.durationMinutes ?? 60,
      authorId: info.event.extendedProps.authorId ?? null,
      authorName: info.event.extendedProps.authorName ?? null,
      canManage: info.event.extendedProps.canManage ?? false,
      montageStatusId: info.event.extendedProps.montageStatusId ?? null,
      montageStatusName: info.event.extendedProps.montageStatusName ?? null,
      csmOwnerId: info.event.extendedProps.csmOwnerId ?? null,
      csmOwnerName: info.event.extendedProps.csmOwnerName ?? null,
      aeName: info.event.extendedProps.aeName ?? null,
      sdrName: info.event.extendedProps.sdrName ?? null,
    });
  };

  const onDayModalRowClick = (event: EventInput) => {
    if ((event as any).restricted) return;

    openMeetingModal({
      id: (event.id as string) ?? "",
      companyId: (event as any).companyId ?? null,
      companyName: (event as any).companyName ?? "Entreprise",
      startAt: event.start as string,
      durationMinutes: (event as any).durationMinutes ?? 60,
      authorId: (event as any).authorId ?? null,
      authorName: (event as any).authorName ?? null,
      canManage: (event as any).canManage ?? false,
      montageStatusId: (event as any).montageStatusId ?? null,
      montageStatusName: (event as any).montageStatusName ?? null,
      csmOwnerId: (event as any).csmOwnerId ?? null,
      csmOwnerName: (event as any).csmOwnerName ?? null,
      aeName: (event as any).aeName ?? null,
      sdrName: (event as any).sdrName ?? null,
    });
  };

  return (
    <div className="formalys-page formalys-calendar-page">
      <MontageMeetingModal ref={ModalRef} />
      {canCreate && (
        <StoreCompanyMeetingModal
          ref={StoreMeetingModalRef}
          calendarType="montage"
        />
      )}
      <CalendarBoard
        name="montage"
        events={events}
        isLoading={isLoading}
        isFetching={isFetching}
        onEventClick={onEventClick}
        onDayModalRowClick={onDayModalRowClick}
        onSlotClick={
          canCreate
            ? (start) => StoreMeetingModalRef.current?.openAt(start)
            : undefined
        }
        headerSearch={
          <Col xs={12} md={3} className="order-md-2 mb-2 mb-md-0 mx-0 mx-md-3">
            <Form.Control
              type="search"
              placeholder="Rechercher une entreprise ou un CSM..."
              size="sm"
              aria-label="Filtrer les rendez-vous montage"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
            />
          </Col>
        }
        headerActions={
          <>
            {canCreate && (
              <IconButton
                variant="nolina-primary"
                size="sm"
                icon={faPlus}
                transform="shrink-3"
                iconAlign="middle"
                className="me-2"
                aria-label="Nouveau rendez-vous"
                onClick={() =>
                  StoreMeetingModalRef.current?.showOrHiddenModal()
                }
              >
                <span className="ms-1 d-none d-sm-inline-block">Nouveau</span>
              </IconButton>
            )}
            <CalendarFiltersButton
              count={countActiveMontageCalendarFilters(filters)}
              onClick={() => setShowFilters(true)}
            />
            {legend}
          </>
        }
      />
      <MontageCalendarFiltersPanel
        show={showFilters}
        onHide={() => setShowFilters(false)}
        filters={filters}
        onFiltersChange={onFiltersChange}
        showUserFilters={showUserFilters}
        userOptions={seesEveryone ? "all" : "pool"}
      />
    </div>
  );
};

export default CalendarMontageView;
